import { nlsfbGroupCode } from "./nlsfb.js";

/**
 * Assettypes ("Wat is het?", docs/SYMBOLEN.md): een vaste Klantpunt-lijst die voor elke organisatie
 * geldt. Het type bepaalt het symbool en vult het NL-SfB-element (2 cijfers) in als dat nog leeg is.
 * Eerste stap van de assetbibliotheek. Een nieuw type krijgt altijd een eigen symbool in
 * `apps/web/src/features/assets/symbols.tsx`.
 */
export const ASSET_TYPES = [
  "heating_boiler",
  "heat_pump",
  "air_conditioner",
  "radiator",
  "chiller",
  "buffer_tank",
  "ventilation_unit",
  "exhaust_fan",
  "vent_grille",
  "sprinkler",
  "fire_hose_reel",
  "fire_extinguisher",
  "smoke_vent",
  "smoke_detector",
  "fire_alarm_panel",
  "access_control",
  "camera",
  "emergency_light",
  "exit_sign",
  "main_switchboard",
  "distribution_board",
  "solar_panels",
  "generator",
  "transformer",
  "lightning_rod",
  "intercom",
  "data_cabinet",
  "lift",
  "overhead_door",
  "water_heater",
  "pump",
  "facade_access",
] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export interface AssetTypeInfo {
  key: AssetType;
  label: string;
  /** NL-SfB-element (2 cijfers) dat het type invult. */
  element: string;
  /** Diepere NL-SfB-codes die op dit type wijzen (voorstel bij organisaties met de volledige tabel). */
  codes: readonly string[];
  /** Woorden in de assetnaam die op dit type wijzen (kleine letters, zonder leestekens). */
  words: readonly string[];
}

const T = (key: AssetType, label: string, element: string, codes: string[], words: string[]): AssetTypeInfo => ({
  key,
  label,
  element,
  codes,
  words,
});

/** Op volgorde van de kiezer: verwarming en koeling, lucht, brand, beveiliging, elektra, overig. */
export const ASSET_TYPE_LIST: readonly AssetTypeInfo[] = [
  T(
    "heating_boiler",
    "Ketel",
    "56",
    ["56.24", "56.25", "56.11", "56.13"],
    ["ketel", "cvketel", "cv", "hrketel", "stoomketel"],
  ),
  T("heat_pump", "Warmtepomp", "56", ["56.21"], ["warmtepomp", "wp", "lucht/water", "luchtwater"]),
  T(
    "air_conditioner",
    "Airco / split-unit",
    "56",
    ["56.14", "56.15"],
    ["airco", "airconditioning", "split", "splitunit", "vrf"],
  ),
  T(
    "radiator",
    "Radiator / convector",
    "56",
    ["56.61", "56.62", "56.66", "56.6"],
    ["radiator", "radiatoren", "convector", "convectoren", "fancoil", "fancoilunit"],
  ),
  T("chiller", "Koelmachine", "56", ["56.22", "56.23"], ["koelmachine", "chiller", "koelunit"]),
  T("buffer_tank", "Buffervat", "56", ["56.5", "56.51", "56.52", "56.53"], ["buffervat", "buffer", "expansievat"]),
  T(
    "ventilation_unit",
    "Ventilatie-unit / WTW",
    "57",
    ["57.13", "57.21", "57.2"],
    ["wtw", "lbk", "luchtbehandelingskast", "ventilatieunit", "luchtbehandeling", "balansventilatie"],
  ),
  T(
    "exhaust_fan",
    "Afzuigventilator",
    "57",
    ["57.14", "57.22"],
    ["afzuiging", "afzuigventilator", "dakventilator", "mv", "mvbox", "afzuigkap"],
  ),
  T(
    "vent_grille",
    "Ventilatierooster",
    "57",
    ["57.4", "57.41", "57.42"],
    ["rooster", "roosters", "ventilatierooster", "ventiel", "ventielen"],
  ),
  T(
    "sprinkler",
    "Sprinkler",
    "55",
    ["55.2", "55.21", "55.22"],
    ["sprinkler", "sprinklerinstallatie", "sprinklerkop", "watermist"],
  ),
  T("fire_hose_reel", "Brandslanghaspel", "55", ["55.24"], ["brandslanghaspel", "slanghaspel", "haspel", "haspels"]),
  T(
    "fire_extinguisher",
    "Brandblusser",
    "55",
    [],
    ["brandblusser", "blusser", "blussers", "blusmiddel", "blusmiddelen", "blusdeken"],
  ),
  T(
    "smoke_vent",
    "RWA / rookluik",
    "55",
    ["55.3", "55.31", "55.32"],
    ["rwa", "rookluik", "rookluiken", "rwaluik", "rwaluiken", "brandventilatie"],
  ),
  T("smoke_detector", "Rookmelder", "65", ["65.11"], ["rookmelder", "rookmelders", "melder", "melders", "detector"]),
  T(
    "fire_alarm_panel",
    "Brandmeldcentrale",
    "65",
    ["65.1", "65.12", "65.13"],
    ["brandmeldcentrale", "bmc", "brandmeldinstallatie", "bmi", "ontruimingsalarm", "ontruimingsinstallatie", "oai"],
  ),
  T(
    "access_control",
    "Toegangscontrole",
    "65",
    ["65.23", "65.2", "65.21", "65.22"],
    ["toegangscontrole", "kaartlezer", "inbraakalarm", "inbraakmeldcentrale", "alarmsysteem"],
  ),
  T("camera", "Camera", "65", ["65.24", "64.31"], ["camera", "cameras", "cctv", "camerasysteem"]),
  T(
    "emergency_light",
    "Noodverlichting",
    "63",
    ["63.22", "61.11"],
    ["noodverlichting", "noodarmatuur", "noodarmaturen", "noodlicht"],
  ),
  T(
    "exit_sign",
    "Vluchtwegaanduiding",
    "63",
    ["63.4", "63.41", "63.42", "63.43", "63.44"],
    ["vluchtwegaanduiding", "vluchtweg", "nooduitgang", "exit", "transparant"],
  ),
  T(
    "main_switchboard",
    "Hoofdverdeler",
    "61",
    ["61.5", "61.51", "61.52", "61.4", "61.42"],
    ["hoofdverdeler", "hoofdverdeelinrichting", "hvi", "hoofdschakelaar"],
  ),
  T(
    "distribution_board",
    "Groepenkast",
    "61",
    ["61.54", "61.53"],
    ["groepenkast", "verdeler", "onderverdeler", "verdeelkast", "verdeelinrichting"],
  ),
  T(
    "solar_panels",
    "Zonnepanelen",
    "61",
    ["61.15"],
    ["zonnepanelen", "zonnepaneel", "pv", "pvinstallatie", "omvormer", "zonnestroom"],
  ),
  T(
    "generator",
    "Noodstroomaggregaat",
    "61",
    ["61.13", "61.12", "61.14"],
    ["aggregaat", "noodstroom", "noodstroomaggregaat", "ups", "nobreak"],
  ),
  T("transformer", "Transformator", "61", ["61.18", "61.44"], ["transformator", "trafo", "trafostation"]),
  T("lightning_rod", "Bliksemafleider", "61", ["61.26", "61.2"], ["bliksemafleider", "bliksembeveiliging", "aarding"]),
  T(
    "intercom",
    "Intercom",
    "64",
    ["64.22", "64.21"],
    ["intercom", "deurbel", "videofoon", "parlofoon", "spreekluister"],
  ),
  T(
    "data_cabinet",
    "Data- / patchkast",
    "64",
    ["64.5", "64.51", "64.52", "64.55"],
    ["patchkast", "datakast", "serverkast", "19inch", "netwerkkast", "serverruimte"],
  ),
  T(
    "lift",
    "Lift",
    "66",
    ["66.1", "66.11", "66.12", "66.13", "66.14", "66.31"],
    ["lift", "liften", "personenlift", "goederenlift", "traplift"],
  ),
  T(
    "overhead_door",
    "Overheaddeur / poort",
    "31",
    ["31.6", "31.61", "31.62", "31.63", "32.6", "32.61", "32.62", "32.63"],
    ["overheaddeur", "sectionaaldeur", "roldeur", "snelloopdeur", "poort", "kanteldeur", "rolluik"],
  ),
  T(
    "water_heater",
    "Boiler",
    "53",
    ["53.2", "53.21", "53.22"],
    ["boiler", "close-in", "closein", "geiser", "warmwater", "tapwater"],
  ),
  T(
    "pump",
    "Pomp / drukverhoging",
    "53",
    ["53.16", "53.26", "53.36", "52.16", "52.26", "52.36", "52.46"],
    ["pomp", "pompen", "drukverhoging", "hydrofoor", "rioolpomp", "dompelpomp", "circulatiepomp"],
  ),
  T(
    "facade_access",
    "Glazenwasinstallatie",
    "75",
    ["75.11", "75.1"],
    [
      "glazenwasinstallatie",
      "gevelonderhoudsinstallatie",
      "gondel",
      "davit",
      "valbeveiliging",
      "ankerpunt",
      "ankerpunten",
    ],
  ),
];

const byKey = new Map(ASSET_TYPE_LIST.map((t) => [t.key, t]));
const byCode = new Map(ASSET_TYPE_LIST.flatMap((t) => t.codes.map((c) => [c, t] as const)));

export const isAssetType = (v: unknown): v is AssetType => typeof v === "string" && byKey.has(v as AssetType);
export const assetTypeInfo = (t: string | null | undefined) => (t ? byKey.get(t as AssetType) : undefined);
export const assetTypeLabel = (t: string | null | undefined) => assetTypeInfo(t)?.label ?? null;

/** Element dat het type invult, alleen als de asset nog geen element heeft. */
export function elementForType(type: string | null | undefined, current: string | null | undefined): string | null {
  if (current) return current;
  return assetTypeInfo(type)?.element ?? null;
}

const words = (s: string) =>
  s
    .toLowerCase()
    .replace(/[-_/]/g, "")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/**
 * Voorstel voor het type van een bestaande asset: eerst de NL-SfB-code (van diep naar ondiep), dan
 * woorden in de naam die bij het element passen (of overal, zonder element). Nooit stil opslaan:
 * de gebruiker neemt het voorstel over.
 */
export function suggestAssetType(
  name: string | null | undefined,
  elementCode: string | null | undefined,
): AssetType | null {
  if (elementCode) {
    for (let n = elementCode.length; n >= 4; n--) {
      const t = byCode.get(elementCode.slice(0, n));
      if (t) return t.key;
    }
  }
  const el = elementCode ? nlsfbGroupCode(elementCode) : null;
  const ws = words(name ?? "");
  // Ook samengestelde namen: "cv-ketel" → "cvketel", "rwa-luiken" → "rwaluiken".
  const joined = (name ?? "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
  for (const t of ASSET_TYPE_LIST) {
    if (el && t.element !== el) continue;
    if (t.words.some((w) => ws.includes(w) || (w.length >= 6 && joined.includes(w)))) return t.key;
  }
  return null;
}

/** Zoeken in de kiezer: op naam en trefwoorden. */
export function searchAssetTypes(q: string): readonly AssetTypeInfo[] {
  const s = q
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");
  if (!s) return ASSET_TYPE_LIST;
  return ASSET_TYPE_LIST.filter(
    (t) =>
      t.label
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, "")
        .includes(s) || t.words.some((w) => w.startsWith(s)),
  );
}
